import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLeavePolicy } from "../../contexts/LeavePolicyContext";
import Modal from "../../components/UI/Modal";
import LeavePolicyForm from "../../components/Form/LeavePolicyForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import { Description } from "@headlessui/react";
import Skeleton from "react-loading-skeleton";
import { X, Briefcase, MoreVertical, Building, Calendar, Users, MapPin, Award, Clock, Grid, Search } from 'lucide-react';
import DropdownMenu from "../../components/UI/DropdownMenu";
import ReactPaginate from 'react-paginate';

// Separate component for actions column to avoid hooks rule violation
const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, openModalForEdit, openReasonModal }) => {
  const buttonRef = useRef(null);
  
  return (
    <div className="flex items-center justify-center relative">
      <button
        ref={buttonRef}
        onClick={() =>
          setOpenMenu(openMenu === row.id ? null : row.id)
        }
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
      </button>

      {openMenu === row.id && (
        <DropdownMenu
          buttonRef={buttonRef}
          onClose={() => setOpenMenu(null)}
        >
          <button
            onClick={(e) => {
              openModalForEdit(row);
              setOpenMenu(null);
            }}
            className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            Edit
          </button>
          {(row?.deleted_at || row?.is_trashed) && (
            <button
              onClick={() => {
                openReasonModal("restore", row.id);
                setOpenMenu(null);
              }}
              className="w-full text-left px-3 py-2 text-sm text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
            >
              Restore
            </button>
          )}
          <DeleteButton
            style={`text-red-600 hover:text-red-800 px-3 py-2`}
            onDelete={(e) => {
              openReasonModal("delete", row.id);
              setOpenMenu(null);
            }}
          />
        </DropdownMenu>
      )}
    </div>
  );
});

const LeavePolicyMaster = React.memo(() => {
  const { policies, fetchPolicies, addPolicy, updatePolicy, deletePolicy, restorePolicy, isLoading, pagination, fetchPolicyAudits } = useLeavePolicy();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [message, setMessage] = useState(""); // Success message
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [notification, setNotification] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [reasonAction, setReasonAction] = useState(null); // 'delete' | 'restore'
  const [reasonTargetId, setReasonTargetId] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [auditItems, setAuditItems] = useState([]);
  // const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, perPage: 10, totalItems: 0 });

  // console.log("pagination", pagination);

  useEffect(() => {
    setIsClient(true); // Ensures document.body is available
  }, []);

  const formatDate = (value) => {
    if (!value) return "";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "";
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${day}-${month}-${year}`;
    } catch (_) {
      return "";
    }
  };

  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingPolicy(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const openModalForEdit = useCallback((policy) => {
    setEditingPolicy(policy);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingPolicy(null);
    setFormErrors({});
    setMessage("");
  }, []);

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  const handleFormSubmit = useCallback(
    async (policy) => {
      setLoading(true);
      try {
        if (editingPolicy) {
          const policyData = policy instanceof FormData ? convertFormDataToObject(policy) : policy;
          const result = await updatePolicy({ ...editingPolicy, ...policyData });
          if (result?.success) {
            setMessage("Policy updated successfully.");
            setNotification({ message: "Policy updated successfully.", type: "success" });
            closeModal();
          } else {
            setFormErrors(result?.errors || {});
            setNotification({ message: result?.message || "Failed to update policy.", type: "error" });
          }
        } else {
          const result = await addPolicy(policy);
          if (result?.success) {
            setMessage("Policy added successfully.");
            setNotification({ message: "Policy added successfully.", type: "success" });
            closeModal();
          } else {
            setFormErrors(result?.errors || {});
            setNotification({ message: result?.message || "Failed to add policy.", type: "error" });
          }
        }
      } catch (err) {
        setNotification({ message: `Unexpected error during submission: ${err?.message || err}`, type: "error" });
      } finally {
        setLoading(false);
      }
    },
    [editingPolicy, addPolicy, updatePolicy, closeModal]
  );

  // Memoize campaigns for dropdown options
//   const departmentOptions = useMemo(
//     () => departments.map((c) => ({ value: c.id, label: c.name })),
//     [departments]
//   );

  // Prepare data for table (show policy key)
  const policyForTable = useMemo(
    () => policies.map((policy) => ({ ...policy })),
    [policies]
  );

  const filteredPolicies = useMemo(() => {
    return policyForTable.filter((item) => {
      const searchTerm = searchQuery.toLowerCase();
      return item.key.toLowerCase().includes(searchTerm);
    });
  }, [policyForTable, searchQuery]);

  const handleDeletePolicy = async (id) => {
    try {
      const result = await deletePolicy(id, reasonText ? { reason: reasonText } : {});
      if (result?.success) {
        setNotification({ message: "Policy deleted (soft) successfully", type: "success" });
      } else {
        setNotification({ message: result?.message || "Failed to delete policy", type: "error" });
      }
    } catch (error) {
      setNotification({ message: "Failed to delete policy", type: "error" });
    }
  };

  const handleRestorePolicy = async (id, body = {}) => {
    try {
      const result = await restorePolicy(id, reasonText ? { reason: reasonText } : {});
      if (result?.success) {
        setNotification({ message: "Policy restored successfully", type: "success" });
      } else {
        setNotification({ message: result?.message || "Failed to restore policy", type: "error" });
      }
    } catch (error) {
      setNotification({ message: "Failed to restore policy", type: "error" });
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchPolicies(newPage);
    }
  };

  const openReasonModal = (action, id) => {
    setReasonAction(action);
    setReasonTargetId(id);
    setReasonText("");
    setIsReasonModalOpen(true);
  };

  const closeReasonModal = () => {
    setIsReasonModalOpen(false);
    setReasonAction(null);
    setReasonTargetId(null);
    setReasonText("");
  };

  const confirmReason = async () => {
    if (!reasonAction || !reasonTargetId) return;
    if (reasonAction === "delete") {
      await handleDeletePolicy(reasonTargetId);
    } else if (reasonAction === "restore") {
      await handleRestorePolicy(reasonTargetId);
    }
    closeReasonModal();
  };

  const openAudits = async (policyId, page = 1) => {
    const res = await fetchPolicyAudits(policyId, { page, per_page: 10 });
    if (res?.success) {
      setAuditItems(res.data);
      // setAuditPagination(res.pagination);
      setAuditModalOpen(true);
    } else {
      setNotification({ message: res?.message || "Failed to load audits", type: "error" });
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      { label: "Key", field: "key" },
      { label: "Scope", field: "scope" },
      { label: "Effective From", field: "effective_from", render: (row) => formatDate(row.effective_from) },
      { label: "Effective To", field: "effective_to", render: (row) => formatDate(row.effective_to) },
      {
        label: "Audits",
        field: "audit",
        render: (row) => (
          <button
            className="text-xs text-indigo-600 hover:underline hover:underline-offset-4"
            onClick={() => openAudits(row.id)}
          >
            View Audits
          </button>
        ),
      },
      { label: "Is Active", field: "is_active", render: (row) => (row.is_active ? "True" : "False") },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <ActionsColumn
            row={row}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            openModalForEdit={openModalForEdit}
            openReasonModal={openReasonModal}
          />
        ),
      }
    ],
    [
      openMenu,
      openModalForEdit,
      openReasonModal,
    ]
  );

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-black dark:text-white">Policy Masters</h2>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        {/* Left End: Search Bar */}
        <div className="relative w-full sm:max-w-xl"> 
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by key..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-poppins"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Right End: Add Button */}
        <button
          onClick={openModalForAdd}
          className="bg-blue-600 font-medium text-white px-4 py-2 rounded-md hover:bg-blue-700 whitespace-nowrap"
        >
          Add Policy
        </button>
      </div>

      {/* Success Message */}
      {message && (
        <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-md">
          {message}
        </div>
      )}

      {/* Skeleton Loader */}
      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} height={40} />
          ))}
        </div>
      ) : (
        <div>
          <ReusableTable
            data={filteredPolicies}
            // data={requests.slice().reverse()}
            loading={isLoading}
            columns={columns}
            filterable={false}
            excludeFilterColumns={["actions", "audit"]}
            // excludeFilterColumns={excludeFilterColumns}
          />
        </div>
      )}

    <ReactPaginate
        breakLabel="..."
        onPageChange={(event) => handlePageChange(event.selected + 1)}
        forcePage={currentPage - 1}
        pageRangeDisplayed={5}
        pageCount={pagination.totalPages}
        renderOnZeroPageCount={null}
        nextLabel="Next >"
        previousLabel="< Prev"
        previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800'
        nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
        containerClassName="flex items-center justify-center mt-4 font-poppins"
        pageClassName="mx-1 px-3 py-1 border rounded dark:text-white font-poppins"
        activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
    />

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingPolicy ? "Edit Policy" : "Add Policy"}
      >
        <LeavePolicyForm
          loading={loading} 
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          initialValues={
            editingPolicy || {
              key: "",
              scope: "hr",
              value: {
                enabled: false,
                count_sandwiched_days_as_leave: false,
                treat_weekend_or_public_holiday_between_leaves_as_leave: false,
                minimum_consecutive_leave_days: 1,
                apply_to: [],
                notes:
                  "If a non-working day (weekend/public holiday) falls between two employee leave days, that non-working day will also be counted as leave when this rule is enabled.",
              },
              effective_from: "",
              effective_to: "",
              is_active: true,
            }
          }
          errors={formErrors}
        />
      </Modal>
      {/* Audits Modal */}
      <Modal
        isOpen={auditModalOpen}
        onRequestClose={() => setAuditModalOpen(false)}
        title="Policy Audits"
      >
        <div className="space-y-3 max-h-96 overflow-auto">
          {auditItems.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-300">No audit entries found.</p>
          ) : (
            auditItems.map((a, idx) => (
              <div key={idx} className="border rounded p-2 dark:border-gray-700">
                <div className="text-sm dark:text-white"><span className="font-medium">Event:</span> {a.event || a.action || "-"}</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">At: {formatDate(a.created_at) || "-"}</div>
                {a.user && (
                  <div className="text-sm text-gray-600 dark:text-gray-300">By: {a.user.name || a.user_id}</div>
                )}
                {a.reason && (
                  <div className="text-sm text-gray-600 dark:text-gray-300">Reason: {a.reason}</div>
                )}
              </div>
            ))
          )}
        </div>
        <div className="mt-3">
          {/* <ReactPaginate
            breakLabel="..."
            onPageChange={(event) => openAudits(auditItems[0]?.policy_id, event.selected + 1)}
            forcePage={auditPagination.currentPage - 1}
            pageRangeDisplayed={5}
            pageCount={auditPagination.totalPages || 1}
            renderOnZeroPageCount={null}
            nextLabel="Next >"
            previousLabel="< Prev"
            previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800'
            nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
            containerClassName="flex items-center justify-center mt-4 font-poppins"
            pageClassName="mx-1 px-3 py-1 border rounded dark:text-white font-poppins"
            activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
          /> */}

          <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            forcePage={currentPage - 1}
            pageRangeDisplayed={5}
            disabledClassName='opacity-50 cursor-not-allowed'
            disabledLinkClassName='pointer-events-none'
            pageCount={pagination.totalPages}
            previousLabel="< Prev"
            previousClassName='dark:text-white border border-gray-300 rounded-md dark:border-gray-600'
            previousLinkClassName='block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
            nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
            nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            renderOnZeroPageCount={null}
            containerClassName="flex items-center justify-center mt-4 font-poppins"
            pageClassName="mx-1 border rounded dark:text-white font-poppins"
            pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
            activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
            activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
          />
        </div>
      </Modal>
      {/* Reason Modal */}
      <Modal
        isOpen={isReasonModalOpen}
        onRequestClose={closeReasonModal}
        title={reasonAction === "restore" ? "Restore Policy" : "Delete Policy"}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {reasonAction === "restore"
              ? "Optionally provide a reason for restoring this policy."
              : "Optionally provide a reason for deleting (soft) this policy."}
          </p>
          <textarea
            className="w-full border rounded-md p-2 dark:bg-black dark:text-white dark:border-gray-700"
            rows={4}
            placeholder="Reason (optional)"
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={closeReasonModal}
              className="px-4 py-2 font-medium bg-gray-500 text-white rounded-md hover:bg-gray-600 dark:bg-gray-900"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmReason}
              className={`px-4 py-2 font-medium text-white rounded-md ${
                reasonAction === "restore" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
              }`}
            >
              Confirm
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
});

export default LeavePolicyMaster;
